import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Alert,
  Badge,
  ListGroup,
  Spinner,
  Card,
  Row,
  Col
} from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import qaService from '../services/qaService';
import { db } from '../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';

const DocumentReviewPanel = ({ show, document, onHide, onReviewComplete }) => {
  const { currentUser } = useAuth();

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [documentDetails, setDocumentDetails] = useState(null);

  // Review form state
  const [reviewAction, setReviewAction] = useState('approve');
  const [rejectionReason, setRejectionReason] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [showReReviewForm, setShowReReviewForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteNotes, setDeleteNotes] = useState('');

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editCategory, setEditCategory] = useState('');

  // Predefined rejection reasons
  const rejectionReasons = [
    'Incomplete information',
    'Poor quality/unreadable',
    'Wrong document type',
    'Expired or outdated',
    'Missing signatures',
    'Does not meet requirements',
    'Fraudulent or altered',
    'Other (specify in notes)'
  ];

  // Document categories for editing
  const documentCategories = [
    'Certified ID Copy - not older than 3 months',
    'Certified Matric Certificate - not older than 3 months',
    'Certified Highest Qualification - not older than 3 months',
    'Proof of Address - not older than 3 months',
    'Completed EEA1 Form',
    'Internship/Learnership Agreement',
    'Banking Details Confirmation',
    'Proof of Disability (if applicable)',
    'Unemployed Affidavit',
    'Other'
  ];

  useEffect(() => {
    if (show && document) {
      loadDocumentDetails();
    }
  }, [show, document]);

  const loadDocumentDetails = async () => {
    try {
      setLoading(true);
      setError('');

      // Use docPath if available (for collectionGroup documents), otherwise use id
      const documentIdentifier = document.docPath || document.id;
      const details = await qaService.getDocumentById(documentIdentifier);
      setDocumentDetails(details);
      setEditCategory(details.category || '');
      setShowReReviewForm(false);
      setIsEditing(false);
    } catch (err) {
      devError('Error loading document details:', err);
      setError('Failed to load document details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      setError('');

      // Use docPath for subcollection documents, otherwise use id
      const documentIdentifier = document.docPath || document.id;
      let docRef;
      if (documentIdentifier.includes('/')) {
        docRef = doc(db, documentIdentifier);
      } else {
        docRef = doc(db, 'documents', documentIdentifier);
      }

      await updateDoc(docRef, {
        category: editCategory,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      });

      // Reload document details
      await loadDocumentDetails();
      setIsEditing(false);

      // Notify parent to refresh
      if (onReviewComplete) {
        onReviewComplete();
      }
    } catch (err) {
      devError('Error saving document edit:', err);
      setError('Failed to save changes: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    if (reviewAction === 'reject' && !rejectionReason) {
      setError('Please select a rejection reason');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Use docPath for subcollection documents, otherwise use id
      const documentIdentifier = document.docPath || document.id;

      if (reviewAction === 'approve') {
        await qaService.approveDocument(documentIdentifier, currentUser.uid, reviewNotes);
      } else {
        await qaService.rejectDocument(
          documentIdentifier,
          currentUser.uid,
          rejectionReason,
          reviewNotes
        );
      }

      // Reset form
      setReviewAction('approve');
      setRejectionReason('');
      setReviewNotes('');

      // Callback to parent
      if (onReviewComplete) {
        onReviewComplete();
      }
    } catch (err) {
      devError('Error submitting review:', err);
      setError('Failed to submit review: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkMissing = async () => {
    if (!deleteReason) {
      setError('Please select a reason before marking this document missing.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const documentIdentifier = document.docPath || document.id;
      await qaService.markDocumentMissing(
        documentIdentifier,
        currentUser.uid,
        deleteReason,
        deleteNotes
      );

      setShowDeleteModal(false);
      setDeleteReason('');
      setDeleteNotes('');

      if (onReviewComplete) {
        onReviewComplete();
      }
      onHide();
    } catch (err) {
      devError('Error marking document missing:', err);
      setError('Failed to mark document missing: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_review: { variant: 'warning', text: 'Pending Review' },
      qa_approved: { variant: 'success', text: 'Approved' },
      qa_rejected: { variant: 'danger', text: 'Rejected' },
      valid: { variant: 'success', text: 'Valid' },
      invalid: { variant: 'danger', text: 'Invalid' },
      warning: { variant: 'warning', text: 'Warning' }
    };

    const badge = badges[status] || { variant: 'secondary', text: status || 'Unknown' };
    return <Badge bg={badge.variant}>{badge.text}</Badge>;
  };

  const formatBytes = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  if (!document) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>📋 Document Review</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        {loading && !documentDetails ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">Loading document details...</p>
          </div>
        ) : documentDetails ? (
          <>
            {/* Document Information */}
            <Card className="mb-3">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Document Information</h6>
                {!isEditing ? (
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                    >
                      ✏️ Edit
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => setShowDeleteModal(true)}
                    >
                      🗑️ Mark Missing
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleSaveEdit}
                      disabled={loading}
                      className="me-2"
                    >
                      {loading ? 'Saving...' : '✓ Save'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setIsEditing(false);
                        setEditCategory(documentDetails.category || '');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p className="mb-2">
                      <strong>File Name:</strong><br />
                      {documentDetails.fileName || 'Unnamed'}
                    </p>
                    <p className="mb-2">
                      <strong>Category:</strong><br />
                      {isEditing ? (
                        <Form.Select
                          value={editCategory}
                          onChange={(e) => setEditCategory(e.target.value)}
                          size="sm"
                        >
                          <option value="">Select category...</option>
                          {documentCategories.map((cat, idx) => (
                            <option key={idx} value={cat}>{cat}</option>
                          ))}
                        </Form.Select>
                      ) : (
                        documentDetails.category || 'N/A'
                      )}
                    </p>
                    <p className="mb-2">
                      <strong>File Size:</strong><br />
                      {formatBytes(documentDetails.fileSize)}
                    </p>
                    <p className="mb-2">
                      <strong>File Type:</strong><br />
                      {documentDetails.fileType || 'N/A'}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p className="mb-2">
                      <strong>Uploaded By:</strong><br />
                      {documentDetails.userInfo?.displayName || 'Unknown'}
                      <br />
                      <small className="text-muted">{documentDetails.userInfo?.email}</small>
                    </p>
                    <p className="mb-2">
                      <strong>Uploaded At:</strong><br />
                      {formatDate(documentDetails.uploadedAt)}
                    </p>
                    <p className="mb-2">
                      <strong>Validation Status:</strong><br />
                      {getStatusBadge(documentDetails.validationStatus)}
                    </p>
                    <p className="mb-2">
                      <strong>Review Status:</strong><br />
                      {getStatusBadge(documentDetails.reviewStatus)}
                    </p>
                  </Col>
                </Row>

                {documentDetails.downloadURL && (
                  <div className="mt-3">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      href={documentDetails.downloadURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="me-2"
                    >
                      📥 Download Document
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      href={documentDetails.downloadURL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      🔗 Open in New Tab
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Document Preview */}
            {documentDetails.downloadURL && (
              <Card className="mb-3">
                <Card.Header>
                  <h6 className="mb-0">📄 Document Preview</h6>
                </Card.Header>
                <Card.Body className="p-0">
                  {documentDetails.fileType?.startsWith('image/') ? (
                    // Image preview
                    <div style={{ maxHeight: '600px', overflow: 'auto', textAlign: 'center', padding: '20px', background: '#f8f9fa' }}>
                      <img
                        src={documentDetails.downloadURL}
                        alt={documentDetails.fileName}
                        style={{ maxWidth: '100%', height: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                      />
                    </div>
                  ) : documentDetails.fileType === 'application/pdf' ? (
                    // PDF preview
                    <div style={{ height: '600px', width: '100%' }}>
                      <iframe
                        src={documentDetails.downloadURL}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="Document Preview"
                      />
                    </div>
                  ) : (
                    // Unsupported file type
                    <div className="text-center py-5">
                      <p className="text-muted mb-3">
                        Preview not available for this file type: {documentDetails.fileType || 'unknown'}
                      </p>
                      <Button
                        variant="primary"
                        href={documentDetails.downloadURL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open Document
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* Flagging Information */}
            {documentDetails.flaggedForReview && (
              <Card className="mb-3 border-warning">
                <Card.Header className="bg-warning text-dark">
                  <h6 className="mb-0">Flagged for Review</h6>
                </Card.Header>
                <Card.Body>
                  <p className="mb-2">
                    <strong>Reason:</strong><br />
                    {documentDetails.flaggedReason || 'Not specified'}
                  </p>
                  <p className="mb-0">
                    <strong>Flagged At:</strong><br />
                    {formatDate(documentDetails.flaggedAt)}
                  </p>
                </Card.Body>
              </Card>
            )}

            {/* Validation Results */}
            {documentDetails.validationResult && (
              <Card className="mb-3">
                <Card.Header>
                  <h6 className="mb-0">Validation Results</h6>
                </Card.Header>
                <Card.Body>
                  {documentDetails.validationResult.errors?.length > 0 && (
                    <div className="mb-3">
                      <strong className="text-danger">Errors:</strong>
                      <ListGroup variant="flush">
                        {documentDetails.validationResult.errors.map((error, idx) => (
                          <ListGroup.Item key={idx} className="text-danger">
                            {error.message}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </div>
                  )}

                  {documentDetails.validationResult.warnings?.length > 0 && (
                    <div className="mb-3">
                      <strong className="text-warning">Warnings:</strong>
                      <ListGroup variant="flush">
                        {documentDetails.validationResult.warnings.map((warning, idx) => (
                          <ListGroup.Item key={idx} className="text-warning">
                            {warning.message}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </div>
                  )}

                  {/* OCR Results */}
                  {documentDetails.validationResult.ocr && (
                    <div className="mb-3">
                      <strong>OCR Legibility Analysis:</strong>
                      <Card className="mt-2 bg-light border-0">
                        <Card.Body>
                          <Row>
                            <Col md={6}>
                              <p className="mb-2">
                                <strong>Status:</strong>{' '}
                                <Badge bg={documentDetails.validationResult.ocr.isLegible ? 'success' : 'warning'}>
                                  {documentDetails.validationResult.ocr.isLegible ? 'Legible' : 'Needs Review'}
                                </Badge>
                              </p>
                              <p className="mb-2">
                                <strong>Confidence:</strong>{' '}
                                {(documentDetails.validationResult.ocr.confidence * 100).toFixed(1)}%
                              </p>
                              <p className="mb-2">
                                <strong>Words Detected:</strong> {documentDetails.validationResult.ocr.wordCount}
                              </p>
                            </Col>
                            <Col md={6}>
                              {documentDetails.validationResult.ocr.lowConfidenceWords > 0 && (
                                <p className="mb-2 text-warning">
                                  <strong>Low Confidence Words:</strong>{' '}
                                  {documentDetails.validationResult.ocr.lowConfidenceWords}
                                </p>
                              )}
                              {documentDetails.validationResult.ocr.detectedLanguages &&
                               documentDetails.validationResult.ocr.detectedLanguages.length > 0 && (
                                <p className="mb-2">
                                  <strong>Languages:</strong>{' '}
                                  {documentDetails.validationResult.ocr.detectedLanguages.join(', ')}
                                </p>
                              )}
                            </Col>
                          </Row>
                          {documentDetails.validationResult.ocr.extractedText && (
                            <div className="mt-3">
                              <strong>Extracted Text Preview:</strong>
                              <div
                                className="mt-2 p-2 border rounded bg-white"
                                style={{
                                  maxHeight: '150px',
                                  overflow: 'auto',
                                  fontSize: '0.85rem',
                                  fontFamily: 'monospace'
                                }}
                              >
                                {documentDetails.validationResult.ocr.extractedText.substring(0, 1000)}
                                {documentDetails.validationResult.ocr.extractedText.length > 1000 && '...'}
                              </div>
                            </div>
                          )}
                          {documentDetails.validationResult.ocr.warnings &&
                           documentDetails.validationResult.ocr.warnings.length > 0 && (
                            <div className="mt-3">
                              <strong className="text-warning">OCR Warnings:</strong>
                              <ul className="small mb-0 mt-1">
                                {documentDetails.validationResult.ocr.warnings.map((warning, idx) => (
                                  <li key={idx}>{warning}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </div>
                  )}

                  {!documentDetails.validationResult.errors?.length &&
                   !documentDetails.validationResult.warnings?.length && (
                    <p className="text-success mb-0">No validation issues found</p>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* Review History */}
            {documentDetails.reviewHistory && documentDetails.reviewHistory.length > 0 && (
              <Card className="mb-3">
                <Card.Header>
                  <h6 className="mb-0">Review History</h6>
                </Card.Header>
                <Card.Body>
                  <ListGroup variant="flush">
                    {documentDetails.reviewHistory.map((review, idx) => (
                      <ListGroup.Item key={idx}>
                        <div className="d-flex justify-content-between">
                          <div>
                            <strong>{review.action === 'approved' ? 'Approved' : 'Rejected'}</strong>
                            <br />
                            <small className="text-muted">
                              by {review.reviewerInfo?.displayName || 'Unknown'} on{' '}
                              {formatDate(review.timestamp)}
                            </small>
                          </div>
                          <Badge bg={review.action === 'approved' ? 'success' : 'danger'}>
                            {review.action}
                          </Badge>
                        </div>
                        {review.reason && (
                          <p className="mb-1 mt-2">
                            <strong>Reason:</strong> {review.reason}
                          </p>
                        )}
                        {review.notes && (
                          <p className="mb-0">
                            <strong>Notes:</strong> {review.notes}
                          </p>
                        )}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
              </Card>
            )}

            {/* Review Form - Show if not reviewed, pending review, or re-review requested */}
            {(!documentDetails.reviewStatus ||
              documentDetails.reviewStatus === 'pending_review' ||
              documentDetails.reviewStatus === 'not_reviewed' ||
              showReReviewForm) && (
              <Card>
                <Card.Header className="bg-primary text-white">
                  <h6 className="mb-0">{showReReviewForm ? 'Re-review Document' : 'Submit Review'}</h6>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleSubmitReview}>
                    <Form.Group className="mb-3">
                      <Form.Label>Review Decision</Form.Label>
                      <div>
                        <Form.Check
                          inline
                          type="radio"
                          label="Approve"
                          name="reviewAction"
                          value="approve"
                          checked={reviewAction === 'approve'}
                          onChange={(e) => setReviewAction(e.target.value)}
                        />
                        <Form.Check
                          inline
                          type="radio"
                          label="Reject"
                          name="reviewAction"
                          value="reject"
                          checked={reviewAction === 'reject'}
                          onChange={(e) => setReviewAction(e.target.value)}
                        />
                      </div>
                    </Form.Group>

                    {reviewAction === 'reject' && (
                      <Form.Group className="mb-3">
                        <Form.Label>Rejection Reason *</Form.Label>
                        <Form.Select
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          required
                        >
                          <option value="">Select a reason...</option>
                          {rejectionReasons.map((reason, idx) => (
                            <option key={idx} value={reason}>
                              {reason}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                      <Form.Label>Review Notes (Optional)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Add any additional notes or feedback..."
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                      />
                    </Form.Group>

                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          if (showReReviewForm) {
                            setShowReReviewForm(false);
                          } else {
                            onHide();
                          }
                        }}
                        disabled={loading}
                      >
                        {showReReviewForm ? 'Cancel Re-review' : 'Cancel'}
                      </Button>
                      <Button
                        type="submit"
                        variant={reviewAction === 'approve' ? 'success' : 'danger'}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Submitting...
                          </>
                        ) : (
                          `${reviewAction === 'approve' ? 'Approve' : 'Reject'} Document`
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            )}

            {/* Show existing review if already reviewed */}
            {documentDetails.reviewStatus &&
              documentDetails.reviewStatus !== 'pending_review' &&
              documentDetails.reviewStatus !== 'not_reviewed' &&
              !showReReviewForm && (
              <Card className="mb-3">
                <Card.Header className={`${documentDetails.reviewStatus === 'qa_approved' ? 'bg-success' : 'bg-danger'} text-white`}>
                  <h6 className="mb-0">Document Review Status</h6>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="mb-2">
                        <strong>Status:</strong> {getStatusBadge(documentDetails.reviewStatus)}
                      </p>
                      {documentDetails.reviewedBy && (
                        <p className="mb-2">
                          <strong>Reviewed By:</strong> {documentDetails.reviewedByName || documentDetails.reviewedBy}
                        </p>
                      )}
                      {documentDetails.reviewedAt && (
                        <p className="mb-2">
                          <strong>Reviewed At:</strong> {formatDate(documentDetails.reviewedAt)}
                        </p>
                      )}
                      {documentDetails.rejectionReason && (
                        <p className="mb-2">
                          <strong>Rejection Reason:</strong> {documentDetails.rejectionReason}
                        </p>
                      )}
                      {documentDetails.reviewNotes && (
                        <p className="mb-0">
                          <strong>Notes:</strong> {documentDetails.reviewNotes}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        setShowReReviewForm(true);
                        setReviewAction('approve');
                        setRejectionReason('');
                        setReviewNotes('');
                      }}
                    >
                      🔄 Re-review
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            )}
          </>
        ) : null}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Mark Document Missing</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            This will mark the document as missing/outstanding for the learner. They will need to re-upload.
          </Alert>
          <Form.Group className="mb-3">
            <Form.Label>Reason *</Form.Label>
            <Form.Select
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
            >
              <option value="">Select a reason...</option>
              {rejectionReasons.map((reason, idx) => (
                <option key={idx} value={reason}>
                  {reason}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Notes (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Add notes to show to the learner..."
              value={deleteNotes}
              onChange={(e) => setDeleteNotes(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={loading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleMarkMissing} disabled={loading}>
            {loading ? 'Updating...' : 'Mark Missing'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

export default DocumentReviewPanel;
